/**
 * A question's figure.
 *
 * The URL is signed with a 15-minute TTL, and the review queue is exactly the
 * screen left open for an hour, so a stale link is the normal case rather than
 * an edge case. On a load failure this re-reads the question once to get a
 * fresh URL, silently; only if that also fails does it say so, with a manual
 * retry. What it never does is leave a broken image icon next to a question
 * someone is about to approve.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ImageOff, RefreshCw } from "lucide-react";
import type { QuestionWithContext } from "@exam/shared";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { fetchQuestion } from "../data/client";

export function QuestionFigure({
  question,
  className,
  onRefreshed,
}: {
  question: QuestionWithContext;
  className?: string;
  /** Lets the owning screen keep its copy of the question in step. */
  onRefreshed?: (question: QuestionWithContext) => void;
}) {
  const [url, setUrl] = useState<string | null>(question.figure_url);
  const [state, setState] = useState<"ok" | "refreshing" | "expired">("ok");
  /** One automatic refresh per URL — never a reload loop against a 404. */
  const triedFor = useRef<string | null>(null);

  useEffect(() => {
    setUrl(question.figure_url);
    setState("ok");
    triedFor.current = null;
  }, [question.figure_url]);

  const refresh = useCallback(async () => {
    setState("refreshing");
    try {
      const fresh = await fetchQuestion(question.id);
      onRefreshed?.(fresh);
      if (fresh.figure_url && fresh.figure_url !== url) {
        setUrl(fresh.figure_url);
        setState("ok");
      } else {
        setState("expired");
      }
    } catch {
      setState("expired");
    }
  }, [onRefreshed, question.id, url]);

  if (question.figure_r2_key === null) {
    // The "requires a figure but has none" case is a review gate, not an image.
    return question.requires_figure ? (
      <p className="flex items-center gap-1.5 rounded-md border border-violation/30 bg-violation-soft px-3 py-2 text-xs text-violation">
        <ImageOff className="size-3.5 shrink-0" />
        This question refers to a figure that is not stored.
      </p>
    ) : null;
  }

  if (state === "expired" || !url) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2">
        <ImageOff className="size-3.5 shrink-0 text-ink-faint" />
        <p className="text-xs text-ink-muted">
          The figure link has expired.
        </p>
        <Button size="sm" icon={<RefreshCw />} onClick={() => void refresh()}>
          Reload
        </Button>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt="Figure for this question"
      className={cn(
        "max-h-96 rounded-md border border-border bg-surface",
        state === "refreshing" && "opacity-60",
        className,
      )}
      onError={() => {
        if (triedFor.current === url) {
          setState("expired");
          return;
        }
        triedFor.current = url;
        void refresh();
      }}
    />
  );
}
